import { useInfiniteQuery } from '@tanstack/react-query';

import { PREFIX_URL } from '@/src/api/config';
import { axios } from '@/src/lib/axios/axios';
import { InfiniteQueryConfig } from '@/src/lib/react-query/react-query';
import { BASE_PROJECT_PATH, generateRoutePath } from '@/src/routes';
import { Filter } from '@/src/util/filters';

import {
  CloseReasonCode,
  StatusFilterEnum,
  InboxEntryStatus,
  SortOrder,
} from '../../../types';
import { AllFiles, TreatmentStatusIconEnum } from '../../../types/AllFiles';

export const All_FILES_API_KEY = 'allFiles';

export const getAllFilesKeys = ({ projectId, filter, sort, order }) => [
  All_FILES_API_KEY,
  projectId,
  filter,
  sort,
  order,
];
const url = `${PREFIX_URL}${BASE_PROJECT_PATH}/inbox/allFiles`;

const PAGE_LIMIT = 50;

type AllFilesList = Omit<
  AllFiles,
  'inboxEntryId | treatmentStatusIcon | allowedStatusChanges | allowClose'
> & {
  theadId: number;
  icon: TreatmentStatusIconEnum;
  'allowed-status-changes': AllFiles['allowedStatusChanges'];
  'allow-close': {
    allowed: boolean;
    reason: CloseReasonCode;
  };
  'is-from-messaging': boolean;
  'uploaded-by'?: string;
  document?: {
    conversation?: boolean;
    'uploaded-by'?: string;
  };
};
export type GetAllFilesResponse = {
  page: number;
  limit: number;
  count: number;
  activeCount: number;
  newCount: number;
  files: AllFilesList[];
};

const SORTABLE_COLUMNS = ['dateReceived'];
const SortOrderEnum = {
  asc: 'asc',
  desc: 'desc',
};

function sanitizeFilter(filter: any) {
  return filter.map((f) => ({
    field: f.field,
    conditions: f.conditions,
  }));
}

const getAllFiles = ({
  page,
  projectId,
  filter,
  sort,
  order,
}): Promise<GetAllFilesResponse> => {
  const filterParams = filter
    ? { filter: JSON.stringify(sanitizeFilter(filter)) }
    : {};

  const sortParams = SORTABLE_COLUMNS.includes(sort)
    ? {
        sort: JSON.stringify({
          field: sort,
          order: SortOrderEnum[order] ? order : SortOrderEnum.desc,
        }),
      }
    : {};
  const baseUrl = generateRoutePath(
    url,
    { projectId },
    { ...filterParams, ...sortParams }
  );
  return axios.get(baseUrl, {
    params: {
      page,
      limit: PAGE_LIMIT,
    },
  });
};

type useGetAllFilesParams = {
  projectId: string;
  filter: Filter[];
  sort: string;
  order: SortOrder;
  config?: InfiniteQueryConfig<typeof getAllFiles>;
};

export const useGetAllFiles = ({
  projectId,
  filter,
  sort,
  order,
  config,
}: useGetAllFilesParams) => {
  return useInfiniteQuery({
    queryKey: getAllFilesKeys({ projectId, filter, sort, order }),
    keepPreviousData: true,
    queryFn: ({ pageParam = 1 }) =>
      getAllFiles({ page: pageParam, projectId, filter, sort, order }),
    staleTime: Infinity,
    getNextPageParam: (lastPageData) => {
      const lastPage = Math.ceil(lastPageData.count / PAGE_LIMIT);
      const nextPageNumber = lastPageData.page + 1;
      return nextPageNumber > lastPage ? undefined : nextPageNumber;
    },
    ...config,
  });
};
